import {
  BrainIcon,
  CalendarIcon,
  ChartNoAxesColumnIcon,
  FileSearchIcon,
  FileTextIcon,
  GlobeIcon,
  LightbulbIcon,
  ListChecksIcon,
  MailIcon,
  MessageSquareIcon,
  PaletteIcon,
  PenLineIcon,
  ReceiptIcon,
  SendIcon,
  SparklesIcon,
  UsersIcon,
  WalletIcon,
  ZapIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { MessageKey, Translator } from "~/i18n";
import type { MorrowToolkit } from "../../composioConnect";
import type { MorrowAccountType } from "../../onboardingStore";
import type { MorrowCompanySituation } from "../../morrowCompanyBrief";

export type CompanySituation = "personal" | "marketing" | "product" | "sales" | "general";

export type MorrowLandingProfile = {
  readonly accountType: MorrowAccountType | null;
  readonly companyName: string;
  readonly companyWebsite: string;
  readonly companyBrief?: string;
  readonly companySituation?: MorrowCompanySituation | "";
};

export type LandingSuggestionVisual = {
  /** CSS color stops for the card header gradient. */
  readonly from: string;
  readonly to: string;
  /** Decorative wash behind the hero icon. */
  readonly wash: string;
};

export type LandingSuggestion = {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly prompt: string;
  readonly icon: LucideIcon;
  readonly visual: LandingSuggestionVisual;
  /** Primary Composio toolkit this flow leans on, when any. */
  readonly toolkit?: MorrowToolkit | undefined;
};

const MARKETING =
  /\b(marketing|agency|studio|brand|media|ads?|advertis|creative|growth|\bpr\b|campaign)\b/i;
const PRODUCT =
  /\b(software|saas|app|tech|platform|cloud|devops|developer|api|\bai\b|code|intelligence|agentic|memory|infrastructure)\b/i;
const SALES = /\b(shop|store|commerce|retail|marketplace|sales|ecommerce|e-commerce)\b/i;

/** Host + path, no protocol — enough for a prompt and for matching. */
export function displayCompanyWebsite(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    const host = url.hostname.replace(/^www\./i, "");
    const path = url.pathname.replace(/\/+$/u, "");
    return path && path !== "/" ? `${host}${path}` : host;
  } catch {
    return trimmed;
  }
}

function haystackFor(profile: MorrowLandingProfile): string {
  return `${profile.companyName} ${displayCompanyWebsite(profile.companyWebsite)} ${profile.companyBrief ?? ""}`;
}

export function inferCompanySituation(profile: MorrowLandingProfile): CompanySituation {
  if (
    profile.companySituation === "marketing" ||
    profile.companySituation === "product" ||
    profile.companySituation === "sales" ||
    profile.companySituation === "general"
  ) {
    return profile.companySituation;
  }
  const name = profile.companyName.trim();
  const website = profile.companyWebsite.trim();
  const brief = profile.companyBrief?.trim() ?? "";
  if (profile.accountType === "individual" || (!name && !website && !brief)) return "personal";
  const haystack = haystackFor(profile);
  if (MARKETING.test(haystack)) return "marketing";
  if (PRODUCT.test(haystack)) return "product";
  if (SALES.test(haystack)) return "sales";
  return "general";
}

type SuggestionKind =
  | "income"
  | "expenses"
  | "report"
  | "marketingPlan"
  | "productBrief"
  | "pipeline"
  | "competitors"
  | "thisWeek"
  | "campaign"
  | "brand"
  | "nextShip"
  | "outreach"
  | "week"
  | "email"
  | "decide"
  | "write"
  | "memory"
  | "meetingPrep"
  | "standup"
  | "vaultSearch"
  | "invoices";

/** Simple everyday starters shown as the landing cards (6 total, 3×2 grid). */
const HERO_KINDS: readonly SuggestionKind[] = [
  "income",
  "expenses",
  "thisWeek",
  "email",
  "memory",
  "meetingPrep",
];

const SUGGESTION_ICONS: Record<SuggestionKind, LucideIcon> = {
  income: ChartNoAxesColumnIcon,
  expenses: WalletIcon,
  invoices: ReceiptIcon,
  report: ChartNoAxesColumnIcon,
  marketingPlan: ZapIcon,
  productBrief: FileTextIcon,
  pipeline: UsersIcon,
  competitors: GlobeIcon,
  thisWeek: CalendarIcon,
  campaign: SparklesIcon,
  brand: PaletteIcon,
  nextShip: ListChecksIcon,
  outreach: SendIcon,
  week: ListChecksIcon,
  email: MailIcon,
  decide: LightbulbIcon,
  write: PenLineIcon,
  memory: BrainIcon,
  meetingPrep: CalendarIcon,
  standup: MessageSquareIcon,
  vaultSearch: FileSearchIcon,
};

const SUGGESTION_VISUALS: Record<SuggestionKind, LandingSuggestionVisual> = {
  income: { from: "#dbeafe", to: "#bfdbfe", wash: "rgba(37, 99, 235, 0.2)" },
  expenses: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.2)" },
  invoices: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.2)" },
  report: { from: "#dbeafe", to: "#bfdbfe", wash: "rgba(37, 99, 235, 0.2)" },
  marketingPlan: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.18)" },
  productBrief: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.2)" },
  pipeline: { from: "#cffafe", to: "#a5f3fc", wash: "rgba(8, 145, 178, 0.18)" },
  competitors: { from: "#cffafe", to: "#a5f3fc", wash: "rgba(8, 145, 178, 0.2)" },
  thisWeek: { from: "#cffafe", to: "#a5f3fc", wash: "rgba(8, 145, 178, 0.2)" },
  campaign: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.18)" },
  brand: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.18)" },
  nextShip: { from: "#dbeafe", to: "#bfdbfe", wash: "rgba(37, 99, 235, 0.18)" },
  outreach: { from: "#cffafe", to: "#a5f3fc", wash: "rgba(8, 145, 178, 0.18)" },
  week: { from: "#dbeafe", to: "#bfdbfe", wash: "rgba(37, 99, 235, 0.18)" },
  email: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.2)" },
  decide: { from: "#dbeafe", to: "#bfdbfe", wash: "rgba(37, 99, 235, 0.2)" },
  write: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.18)" },
  memory: { from: "#dbeafe", to: "#bfdbfe", wash: "rgba(37, 99, 235, 0.2)" },
  meetingPrep: { from: "#cffafe", to: "#a5f3fc", wash: "rgba(8, 145, 178, 0.2)" },
  standup: { from: "#e0e7ff", to: "#c7d2fe", wash: "rgba(79, 70, 229, 0.18)" },
  vaultSearch: { from: "#cffafe", to: "#a5f3fc", wash: "rgba(8, 145, 178, 0.2)" },
};

const SUGGESTION_TOOLKITS: Partial<Record<SuggestionKind, MorrowToolkit>> = {
  income: "notion",
  expenses: "notion",
  invoices: "gmail",
  report: "slack",
  marketingPlan: "notion",
  productBrief: "notion",
  pipeline: "gmail",
  thisWeek: "googlecalendar",
  campaign: "notion",
  brand: "notion",
  nextShip: "github",
  outreach: "gmail",
  week: "googlecalendar",
  email: "gmail",
  meetingPrep: "googlecalendar",
  standup: "slack",
  vaultSearch: "notion",
};

const SITUATION_KINDS: Record<CompanySituation, readonly SuggestionKind[]> = {
  personal: HERO_KINDS,
  marketing: HERO_KINDS,
  product: HERO_KINDS,
  sales: HERO_KINDS,
  general: HERO_KINDS,
};

const LABEL_KEYS = {
  income: "landing.suggest.income",
  expenses: "landing.suggest.expenses",
  invoices: "landing.suggest.invoices",
  report: "landing.suggest.report",
  marketingPlan: "landing.suggest.marketingPlan",
  productBrief: "landing.suggest.productBrief",
  pipeline: "landing.suggest.pipeline",
  competitors: "landing.suggest.competitors",
  thisWeek: "landing.suggest.thisWeek",
  campaign: "landing.suggest.campaign",
  brand: "landing.suggest.brand",
  nextShip: "landing.suggest.nextShip",
  outreach: "landing.suggest.outreach",
  week: "landing.suggest.week",
  email: "landing.suggest.email",
  decide: "landing.suggest.decide",
  write: "landing.suggest.write",
  memory: "landing.suggest.memory",
  meetingPrep: "landing.suggest.meetingPrep",
  standup: "landing.suggest.standup",
  vaultSearch: "landing.suggest.vaultSearch",
} as const satisfies Record<SuggestionKind, MessageKey>;

const DESC_KEYS = {
  income: "landing.suggest.incomeDesc",
  expenses: "landing.suggest.expensesDesc",
  invoices: "landing.suggest.invoicesDesc",
  report: "landing.suggest.reportDesc",
  marketingPlan: "landing.suggest.marketingPlanDesc",
  productBrief: "landing.suggest.productBriefDesc",
  pipeline: "landing.suggest.pipelineDesc",
  competitors: "landing.suggest.competitorsDesc",
  thisWeek: "landing.suggest.thisWeekDesc",
  campaign: "landing.suggest.campaignDesc",
  brand: "landing.suggest.brandDesc",
  nextShip: "landing.suggest.nextShipDesc",
  outreach: "landing.suggest.outreachDesc",
  week: "landing.suggest.weekDesc",
  email: "landing.suggest.emailDesc",
  decide: "landing.suggest.decideDesc",
  write: "landing.suggest.writeDesc",
  memory: "landing.suggest.memoryDesc",
  meetingPrep: "landing.suggest.meetingPrepDesc",
  standup: "landing.suggest.standupDesc",
  vaultSearch: "landing.suggest.vaultSearchDesc",
} as const satisfies Record<SuggestionKind, MessageKey>;

const PROMPT_KEYS = {
  income: "landing.suggest.incomePrompt",
  expenses: "landing.suggest.expensesPrompt",
  invoices: "landing.suggest.invoicesPrompt",
  report: "landing.suggest.reportPrompt",
  marketingPlan: "landing.suggest.marketingPlanPrompt",
  productBrief: "landing.suggest.productBriefPrompt",
  pipeline: "landing.suggest.pipelinePrompt",
  competitors: "landing.suggest.competitorsPrompt",
  thisWeek: "landing.suggest.thisWeekPrompt",
  campaign: "landing.suggest.campaignPrompt",
  brand: "landing.suggest.brandPrompt",
  nextShip: "landing.suggest.nextShipPrompt",
  outreach: "landing.suggest.outreachPrompt",
  week: "landing.suggest.weekPrompt",
  email: "landing.suggest.emailPrompt",
  decide: "landing.suggest.decidePrompt",
  write: "landing.suggest.writePrompt",
  memory: "landing.suggest.memoryPrompt",
  meetingPrep: "landing.suggest.meetingPrepPrompt",
  standup: "landing.suggest.standupPrompt",
  vaultSearch: "landing.suggest.vaultSearchPrompt",
} as const satisfies Record<SuggestionKind, MessageKey>;

function usesCompanyContext(kind: SuggestionKind): boolean {
  return (
    kind !== "week" &&
    kind !== "email" &&
    kind !== "decide" &&
    kind !== "write" &&
    kind !== "memory" &&
    kind !== "meetingPrep" &&
    kind !== "standup" &&
    kind !== "vaultSearch" &&
    kind !== "income" &&
    kind !== "expenses" &&
    kind !== "invoices"
  );
}

function suggestionContext(
  t: Translator,
  profile: MorrowLandingProfile,
): { readonly name: string; readonly site: string } {
  const name = profile.companyName.trim() || t("onboarding.teamFallback");
  const website = displayCompanyWebsite(profile.companyWebsite);
  const brief = (profile.companyBrief ?? "").trim().slice(0, 320);
  const site = [
    website ? t("landing.suggest.siteClause", { website }) : "",
    brief ? t("landing.suggest.briefClause", { brief }) : "",
  ].join("");
  return { name, site };
}

function buildSuggestion(
  t: Translator,
  kind: SuggestionKind,
  profile: MorrowLandingProfile,
): LandingSuggestion {
  const { name, site } = suggestionContext(t, profile);
  const toolkit = SUGGESTION_TOOLKITS[kind];
  return {
    id: kind,
    label: t(LABEL_KEYS[kind]),
    description: t(DESC_KEYS[kind]),
    prompt: usesCompanyContext(kind) ? t(PROMPT_KEYS[kind], { name, site }) : t(PROMPT_KEYS[kind]),
    icon: SUGGESTION_ICONS[kind],
    visual: SUGGESTION_VISUALS[kind],
    ...(toolkit ? { toolkit } : {}),
  };
}

export function getMorrowLandingSuggestions(
  t: Translator,
  profile: MorrowLandingProfile,
): ReadonlyArray<LandingSuggestion> {
  const situation = inferCompanySituation(profile);
  return SITUATION_KINDS[situation].map((kind) => buildSuggestion(t, kind, profile));
}
